import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import api from "../../lib/api";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  detail: string;
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await api.post("/auth/forgot-password", null, {
        params: { email },
      });
      setSubmitted(true);
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorResponse>;
      setError(
        axiosErr.response?.data?.detail ||
          "Failed to send reset email. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/files/favicon-512.png"
            alt="Asset Radar logo"
            className="mb-4 h-14 w-14 rounded-2xl shadow-lg shadow-brand-500/30"
          />
          <h1 className="text-2xl font-bold text-surface-100">
            Reset Password
          </h1>
          <p className="mt-1 text-sm text-surface-400">
            {submitted
              ? "Check your email for instructions"
              : "Enter your email to reset your password"}
          </p>
        </div>

        <div className="rounded-2xl border border-surface-700/50 bg-surface-800/60 backdrop-blur-xl p-8 shadow-2xl shadow-black/20">
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
            >
              {error}
            </motion.div>
          )}

          {submitted ? (
            <div className="text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
                <Mail className="h-8 w-8 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-surface-300">
                  If an account exists with{" "}
                  <span className="font-medium text-surface-100">{email}</span>,
                  you will receive a password reset email shortly.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                icon={<Mail className="h-4 w-4" />}
                required
                autoComplete="email"
              />

              <Button
                type="submit"
                isLoading={isLoading}
                className="w-full"
                size="lg"
              >
                Send Reset Link
              </Button>
            </form>
          )}
        </div>

        {!submitted && (
          <p className="mt-6 text-center text-sm text-surface-400">
            Remember your password?{" "}
            <Link
              to="/login"
              className="text-brand-400 hover:text-brand-300 font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        )}
      </motion.div>
    </div>
  );
}
